/**
 * One lead shape for every product-comparison card (Stage Z PR B).
 *
 * Auto Sourcing produces a rich row; `/leads` produces a saved-lead row. Both
 * must render the SAME card, so the differences are normalised here rather than
 * in each page.
 *
 * Hard rules:
 *   * BUY fields only ever come from source keys; SELL fields only from Walmart
 *     keys. Nothing falls back across the boundary (Stage Y P0-A).
 *   * Missing values stay null so the card renders an honest unavailable state.
 *   * Economics are passed through SIGNED and are never recomputed from prices
 *     alone — a saved lead keeps exactly the verdict it was saved with.
 */
import { emptyDealFlowLead } from "@/lib/dealFlow/adapter";

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const str = (v) => {
  const s = typeof v === "string" ? v.trim() : "";
  return s || null;
};

/** True when the saved lead's qualification says identity contradicted. */
function isIdentityRejected(row) {
  return (
    String(row?.qualification || "").toUpperCase() === "IDENTITY_REJECTED" ||
    String(row?.match_state || "").toLowerCase() === "identity_rejected"
  );
}

/**
 * Map a `/leads` row (saved lead) to the shared card model.
 *
 * `id` is derived from the durable lead id when present so React keys and action
 * handlers stay stable across refreshes.
 */
export function mapSavedLeadToCard(row, index = 0) {
  if (!row || typeof row !== "object") return null;

  const itemId = str(row.walmart_item_id) || str(row.item_id);
  const sourceId = str(row.source_id);
  const sourceProductId = str(row.source_product_id);

  // Source side — ONLY source fields.
  const sourceTitle = str(row.source_title) || str(row.title);
  const sourceBrand = str(row.source_brand) || str(row.brand);
  const sourcePrice = num(row.source_price ?? row.source_cost);

  // Walmart side — ONLY Walmart fields.
  //
  // A price without a matched item is a contradiction, not a sell price: the
  // customer cannot sell against a listing we cannot name. Stage X makes a real
  // Walmart item mandatory, so a stored price with no item id is unusable and is
  // reported as unavailable rather than quoted as if it were realizable.
  const walmartTitle = str(row.walmart_title);
  const walmartBrand = str(row.walmart_brand);
  // The saved-lead record historically stored only the source price; the Walmart
  // sell price now persists as `walmart_price` (and older rows may carry
  // `wm_price`). All three names are read so existing leads hydrate instead of
  // rendering a dash for a value we actually hold.
  const walmartPrice = itemId
    ? num(row.walmart_price ?? row.wm_price ?? row.buy_box_price)
    : null;

  const spread =
    sourcePrice !== null && walmartPrice !== null
      ? Math.round((walmartPrice - sourcePrice) * 100) / 100
      : null;

  const identityRejected = isIdentityRejected(row);

  return emptyDealFlowLead({
    id: str(row.lead_id) || str(row.id) || `saved-${index}`,
    // ── Retailer (BUY) ───────────────────────────────────────────────────
    sourceId,
    sourceProductId,
    sourceMerchant: sourceId || null,
    sourceTitle,
    sourceBrand,
    sourceImage: str(row.source_image) || str(row.image_url),
    sourceUrl: str(row.source_url),
    sourcePrice,
    sourceCurrency: "USD",
    // ── Walmart (SELL) ───────────────────────────────────────────────────
    walmartItemId: itemId || null,
    walmartTitle,
    walmartBrand,
    walmartImage: str(row.walmart_image),
    walmartUrl: str(row.walmart_url),
    walmartPrice,
    walmartCurrency: "USD",
    // ── Economics: passed through, never recomputed ──────────────────────
    spread,
    // Saved leads persist the SIGNED economics as `estimated_*` (the field names
    // the save payload sends). Reading only `profit`/`roi`/`margin` left every
    // saved card showing dashes even though the values were stored.
    profit: num(row.profit ?? row.estimated_profit),
    roi: num(row.roi ?? row.estimated_roi),
    margin: num(row.margin ?? row.estimated_margin),
    // ── Evidence ─────────────────────────────────────────────────────────
    upc: str(row.upc),
    gtin: str(row.gtin),
    matchConfidence: num(row.match_confidence),
    matchState: str(row.match_state),
    qualification: str(row.qualification),
    identityRejected,
    // ── Pipeline metadata ────────────────────────────────────────────────
    leadStatus: str(row.status),
    note: str(row.note),
    savedAt: str(row.saved_at),
    createdAt: str(row.saved_at) || str(row.updated_at),
    updatedAt: str(row.updated_at),
    runId: str(row.run_id),
    saved: true,
  });
}

/** Map a saved-lead list, dropping anything unusable. */
export function mapSavedLeadsToCards(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, i) => mapSavedLeadToCard(row, i)).filter(Boolean);
}
